import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

const MOBILE_PATTERN = /^09\d{9}$/;

const normalizeMobile = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("9")) return `0${digits}`;
  if (digits.length === 11 && digits.startsWith("09")) return digits;
  return digits;
};

const InviteContractorMemberDialog = ({
  open,
  onClose,
  onSubmit,
  saving,
  cities = [],
  editMember = null,
}) => {
  const isEdit = Boolean(editMember);
  const [form, setForm] = useState({
    mobile: "",
    firstName: "",
    lastName: "",
    cityIds: [],
    isActive: true,
    canAssistedRegistration: true,
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const cityOptions = useMemo(
    () =>
      (cities || [])
        .map((c) => ({
          id: Number(c.id ?? c.Id ?? c.value ?? c.Value),
          label: c.cityName ?? c.CityName ?? c.name ?? c.Name ?? String(c.id ?? c.Id ?? ""),
        }))
        .filter((c) => Number.isFinite(c.id) && c.id > 0),
    [cities]
  );

  useEffect(() => {
    if (!open) return;
    if (editMember) {
      setForm({
        mobile: editMember.mobile || "",
        firstName: "",
        lastName: "",
        cityIds: (editMember.allowedCityIds || editMember.AllowedCityIds || []).map(Number),
        isActive: editMember.isActive ?? editMember.IsActive ?? true,
        canAssistedRegistration:
          editMember.canAssistedRegistration ??
          editMember.CanAssistedRegistration ??
          true,
      });
    } else {
      setForm({
        mobile: "",
        firstName: "",
        lastName: "",
        cityIds: [],
        isActive: true,
        canAssistedRegistration: true,
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [open, editMember]);

  const selectedCities = cityOptions.filter((c) => form.cityIds.includes(c.id));

  const validate = () => {
    const next = {};
    if (!isEdit) {
      const mobile = normalizeMobile(form.mobile);
      if (!mobile) next.mobile = "شماره موبایل الزامی است.";
      else if (!MOBILE_PATTERN.test(mobile)) next.mobile = "شماره موبایل باید ۱۱ رقم و با 09 شروع شود.";
    }
    return next;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    const payload = isEdit
      ? {
          isActive: form.isActive,
          canAssistedRegistration: form.canAssistedRegistration,
          cityIds: form.cityIds.filter((id) => Number.isFinite(Number(id)) && Number(id) > 0).map(Number),
        }
      : {
          mobile: normalizeMobile(form.mobile),
          firstName: form.firstName?.trim() || undefined,
          lastName: form.lastName?.trim() || undefined,
          cityIds: form.cityIds.filter((id) => Number.isFinite(Number(id)) && Number(id) > 0).map(Number),
        };

    const result = await onSubmit(payload);
    if (result?.error) setSubmitError(result.error);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "ویرایش کارشناس" : "دعوت کارشناس جدید"}</DialogTitle>
      <DialogContent dividers>
        {submitError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}
        {!isEdit ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            اگر حساب کاربری وجود داشته باشد به تیم اضافه می‌شود؛ در غیر این صورت با رمز اولیه
            پیش‌فرض ساخته می‌شود.
          </Alert>
        ) : null}
        <Grid container spacing={2}>
          {!isEdit ? (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="شماره موبایل"
                  value={form.mobile}
                  onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))}
                  fullWidth
                  required
                  error={Boolean(errors.mobile)}
                  helperText={errors.mobile}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="نام"
                  value={form.firstName}
                  onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="نام خانوادگی"
                  value={form.lastName}
                  onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
                  fullWidth
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <TextField label="موبایل" value={form.mobile} fullWidth disabled />
            </Grid>
          )}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={cityOptions}
              value={selectedCities}
              onChange={(_, values) =>
                setForm((p) => ({ ...p, cityIds: values.map((v) => v.id) }))
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="شهرهای مجاز (خالی = همه شهرها)"
                  placeholder="انتخاب شهر"
                />
              )}
            />
          </Grid>
          {isEdit ? (
            <>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                    />
                  }
                  label="فعال"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.canAssistedRegistration}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, canAssistedRegistration: e.target.checked }))
                      }
                    />
                  }
                  label="دسترسی ثبت متقاضی"
                />
              </Grid>
            </>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          انصراف
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "در حال ذخیره..." : isEdit ? "ذخیره" : "دعوت"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteContractorMemberDialog;
